import React from 'react';
import { 
  ListItem, 
  Box, 
  Typography, 
  Divider,
  Avatar,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/lib';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useTranslation } from 'react-i18next';

const DailyCashItem = ({ item, onItemClick }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const getTypeLabel = (type) => {
    switch(type) {
      case "SELL": return t('dailyCash.sellIncome');
      case "MANUAL_CASH_IN": return t('dailyCash.manualIncome');
      case "MANUAL_CASH_OUT": return t('dailyCash.manualExpense');
      case "PURCHASE": return t('dailyCash.purchaseExpense');
      case "SPENDING": return t('dailyCash.spendingExpense');
      default: return type;
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case "SELL": return theme.palette.success.main;
      case "MANUAL_CASH_IN": return theme.palette.success.main;
      case "MANUAL_CASH_OUT": return theme.palette.error.main;
      case "PURCHASE": return theme.palette.error.main;
      case "SPENDING": return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  const isIncomeType = (type) => {
    return ["SELL", "MANUAL_CASH_IN"].includes(type);
  };

  const getFormattedAmount = (amount, type) => {
    const prefix = isIncomeType(type) ? '+' : '-';
    return `${prefix} ${formatCurrency(amount)}`;
  };

  const getDescription = (item) => {
    if (item.type === "SELL" && item.owner) {
      return t('dailyCash.sellIncomeOwner', { owner: item.owner.name });
    } else if (item.type === "PURCHASE") {
      return t('dailyCash.purchaseExpenseLabel');
    } else {
      return getTypeLabel(item.type);
    }
  };

  const renderAvatar = () => {
    const avatarStyle = { 
      bgcolor: getTypeColor(item.type),
      width: 46,
      height: 46,
    };

    // Case 1: If it's a sale to an owner with avatar URL
    if (item.type === "SELL" && item.owner && item.owner.thumb_url) {
      return <Avatar src={item.owner.thumb_url} sx={avatarStyle} />;
    }
    
    // Case 2: If it's a sale without owner or owner without avatar URL
    if (item.type === "SELL") {
      return <Avatar sx={avatarStyle}><PersonIcon sx={{ fontSize: 28 }} /></Avatar>;
    }
    
    // Case 3: Manual income, manual outcome or expense with user avatar
    if (["MANUAL_CASH_IN", "MANUAL_CASH_OUT", "SPENDING"].includes(item.type)) {
      if (item.user && item.user.thumb_url) {
        return <Avatar src={item.user.thumb_url} sx={avatarStyle} />;
      }
      return <Avatar sx={avatarStyle}><PersonIcon sx={{ fontSize: 28 }} /></Avatar>;
    }
    
    // Case 4: If it's a purchase, use provider image or truck icon
    if (item.type === "PURCHASE") {
      if (item.provider && item.provider.thumb_url) {
        return <Avatar src={item.provider.thumb_url} sx={avatarStyle} />;
      }
      return <Avatar sx={avatarStyle}><LocalShippingIcon sx={{ fontSize: 28 }} /></Avatar>;
    }
    
    // Default fallback
    return <Avatar sx={avatarStyle}>{item.type.charAt(0)}</Avatar>;
  };

  return (
    <ListItem 
      component="button"
      onClick={() => onItemClick(item)}
      sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        alignItems: 'flex-start',
        py: 1,
        px: 1,
        cursor: 'pointer',
        border: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
    >
      {/* Left side - Avatar and Time */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        mr: 2,
        minWidth: 46
      }}>
        {renderAvatar()}
        <Typography 
          variant="caption" 
          sx={{ 
            textAlign: 'center',
            color: 'text.secondary'
          }}
        >
          {format(new Date(item.date), 'HH:mm')}
        </Typography>
      </Box>

      {/* Middle - Title, Subtitle and Reason */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        mr: 2
      }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 'medium',
          }}
        >
          {getDescription(item)}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'text.secondary',
          }}
        >
          {item.user.name}
        </Typography>
      </Box>

      {/* Right side - Amount and Payment Type */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        minWidth: 100
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 'bold',
            color: getTypeColor(item.type)
          }}
        >
          {getFormattedAmount(item.amount, item.type)}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'text.secondary',
            textAlign: 'right'
          }}
        >
          {item.payment_type}
        </Typography>
      </Box>
    </ListItem>
  );
};

export default DailyCashItem; 